<template>
  <div>
    <label class="d-block">{{ $t(`commands.data_types.${config.label}`) }}</label>
    <validation-provider
      :name="$t(`commands.data_types.${config.label}`)"
      :rules="showFromAlarm ? 'required_if:cameras_from_alarm,false' : config.rules"
      v-slot="{ errors }"
    >
      <v-select
        v-model="model"
        :options="formattedOptions"
        :filterable="false"
        @search="onSearch"
        :multiple="(multiple || multiple === 1)"
        :clereable="(multiple || multiple === 1)"
        ref="selectable"
        :disabled="fromAlarm"
        @input="onInput"
      >
        <template #list-footer>
          <li class="pagination">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="!hasPrevPage"
              @click="prevPage"
            >
              <feather-icon icon="ChevronLeftIcon" size="18"/>
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="!hasNextPage"
              @click="nextPage"
            >
              <feather-icon icon="ChevronRightIcon" size="18"/>
            </b-button>
          </li>
        </template>
      </v-select>
      <p v-if="config.hint" class="mb-0 small">{{ $t(config.hint) }}</p>
      <p v-if="errors" class="small text-danger">{{ errors[0] }}</p>
    </validation-provider>
    <div class="form-group form-check mt-1" v-if="showFromAlarm">
      <validation-provider vid="cameras_from_alarm">
        <input
          type="checkbox"
          class="form-check-input"
          name="cameras_from_alarm"
          v-model="fromAlarm"
          value="from_alarm"
        />
      </validation-provider>
      <label class="form-check-label">
        {{ $t("commands.form.location.fields.from_alarm") }}
      </label>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import {debounce} from "lodash";
import {BButton} from "bootstrap-vue";
import {EventBus} from "@/libs/event-bus";
import {ValidationProvider} from "vee-validate";
import axios from "@axios";

export default {
  components: {
    vSelect,
    BButton,
    ValidationProvider,
  },
  props: {
    value: Array | Object,
    multiple: Boolean | Number,
    config: Object,
    context: Object,
  },
  data() {
    return {
      model: null,
      fromAlarm: false,
      search: "",
      offset: 0,
      limit: 8,
      currentPage: 1,
      meta: null,
      options: [],
      showFromAlarm: false,
    };
  },
  created() {
    this.showFromAlarm = this.context.flowchartConfig.alarm_compatible;
    this.model = this.value || null;
    this.fromAlarm = this.showFromAlarm && this.value === "from_alarm";
  },
  mounted() {
    EventBus.on("location_field:data", this.onLocationFieldData);
    this.fetchData();
  },
  beforeDestroy() {
    EventBus.off("location_field:data");
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    fromAlarm: {
      handler(value) {
        if (value) {
          this.$emit("input", "from_alarm");
          EventBus.emit("cameras:data", "from_alarm");
          this.model = null;
        } else {
          this.$emit("input", null);
        }
      },
    },
  },
  computed: {
    formattedOptions() {
      return this.options
        .sort((a, b) => a.camera.name.localeCompare(b.camera.name))
        .map((item) => {
          return {
            value: item.id,
            label: item.camera.name,
          }
        });
    },
    hasNextPage() {
      if (this.meta) {
        return this.meta.current_page <= this.meta.last_page
      }
      return false;
    },
    hasPrevPage() {
      if (this.meta) {
        return this.meta.current_page - 1 > 0;
      }
      return false;
    },
  },
  methods: {
    onInput() {
      EventBus.emit("cameras:data", this.model);

      if (this.context.command === 'activate_camera_ptz_preset') {
        const modelCopy = JSON.parse(JSON.stringify(this.model));
        modelCopy.value = modelCopy.id_camera_proxy;
        this.$emit('input', modelCopy);
      } else {
        this.$emit('input', this.model);
      }

    },
    fetchData() {
      return new Promise((resolve, reject) => {

        axios.get('/v1/proxy_camera?camerasOnly=true', {
          params: {
            q: this.search,
            max: this.limit,
            "page[n]": this.currentPage,
            sortBy: "id",
            //sortDesc: true,
          },
        })
          .then(({data}) => {
            this.meta = data.meta;
            this.options = data.data;
            resolve();
          })
          .catch((error) => {
            reject();
            console.log("CamerasSelect: request error", error.response);
          });
      });
    },
    onLocationFieldData(data) {
      if (!this.fromAlarm && !isNaN(data.radius) && !isNaN(data.center.lat) && !isNaN(data.center.lng)) {
        const lat = data.center.lat;
        const lng = data.center.lng;
        const radius = data.radius;
        const fields = "id,camera_id,camera.name";
        const url = `/v1/services/device/get-nearby-devices?lat=${lat}&lng=${lng}&radius=${radius}&fields=${fields}`;

        axios.get(url).then(({data}) => {
          this.model = data.data.map((resp) => {
            return {
              value: resp.camera_id,
              label: resp.name,
            };
          });
          this.$emit("input", this.model);
        });
      }
    },
    onSearch: debounce(async function (query, loading) {
      if (query.length) {
        loading(true);
        this.search = query;
        this.currentPage = 1;
        await this.fetchData();
        loading(false);
      } else {
        this.currentPage = 1;
        this.search = "";
        await this.fetchData();
      }
    }, 500),
    nextPage() {
      this.currentPage += 1;
    },
    prevPage() {
      this.currentPage--;
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}

.pagination button {
  flex-grow: 1;

  &:first-child {
    margin-right: 5px;
  }
}

.pagination button:hover {
  cursor: pointer;
}
</style>
